import {getTestProps} from '@benchling/js-test-utils';
import {createStyles, Group, SegmentedControl, Stack, Text, Tooltip, UnstyledButton} from '@mantine/core';
import React, {useEffect, useMemo, useState} from 'react';

import * as NavConstants from 'constants/NavConstants';
import {
  buildTimelineRows,
  DEFAULT_TIMELINE_RANGE,
  formatTurnDuration,
  getTimelineTicks,
  getTimelineWindow,
  getTurnBoxGeometry,
  TIMELINE_RANGES,
  TimelineRow,
  TimelineWindow,
} from 'llm/chat-library/foundries/AgentFoundryTimelineLayout';
import {openExistingChat} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredSpinner} from 'ui-library/Spinner';

import {
  AgentFoundryTimelineResponse,
  getAgentFoundryTimeline,
  TimelineRange,
  TimelineTurnResponse,
} from './GetAgentFoundryTimeline';

// Layout knobs. Everything about the geometry of the chart is in this block.
const LABEL_GUTTER_WIDTH_PX = 220;
const RULER_HEIGHT_PX = 28;
const ROW_HEIGHT_PX = 48;
const BOX_HEIGHT_PX = 28;
/** A sub-second turn still gets a box this wide so it can be hovered and clicked. */
const MIN_BOX_WIDTH_PX = 8;
/** Space between the chart's left/right edges and the time axis, so edge ruler labels and boxes are not flush with the border. */
const PLOT_INSET_PX = 24;
const SWATCH_SIZE_PX = 12;
/** Space between the chart's left border and the agent swatch, and between the swatch and the name. */
const GUTTER_PADDING_PX = 16;
const GUTTER_GAP_PX = 8;
// Ruler labels are centered on their tick, except past this point where they end at the tick instead so the
// last label is not clipped by the right edge of the chart.
const RULER_LABEL_RIGHT_ALIGN_PERCENT = 94;
/** Index into an agent's shades used for the swatch beside its name (a mid-tone, readable on white). */
const SWATCH_SHADE_INDEX = 6;
const UNNAMED_AGENT_LABEL = 'Unavailable agent';
const UNTITLED_CHAT_LABEL = 'Untitled chat';

const useStyles = createStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.md,
  },
  chart: {
    border: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: theme.radius.md,
    overflow: 'hidden',
    backgroundColor: theme.white,
  },
  line: {
    display: 'flex',
    minWidth: 0,
  },
  rulerLine: {
    height: RULER_HEIGHT_PX,
    backgroundColor: theme.colors.gray[0],
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
  },
  rowLine: {
    height: ROW_HEIGHT_PX,
    borderBottom: `1px solid ${theme.colors.gray[2]}`,
    '&:last-child': {borderBottom: 'none'},
  },
  gutter: {
    flex: `0 0 ${LABEL_GUTTER_WIDTH_PX}px`,
    width: LABEL_GUTTER_WIDTH_PX,
    boxSizing: 'border-box',
    paddingLeft: GUTTER_PADDING_PX,
    paddingRight: GUTTER_PADDING_PX,
    borderRight: `1px solid ${theme.colors.gray[3]}`,
    display: 'flex',
    alignItems: 'center',
    gap: GUTTER_GAP_PX,
    minWidth: 0,
  },
  lane: {
    position: 'relative',
    flex: 1,
    minWidth: 0,
  },
  // The x-axis is mapped onto this area. It is inset from both lane edges, and stops MIN_BOX_WIDTH_PX further
  // short of the right edge so a minimum-width box that starts at "now" still fits inside the lane.
  plot: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: PLOT_INSET_PX,
    right: PLOT_INSET_PX + MIN_BOX_WIDTH_PX,
  },
  swatch: {
    flex: `0 0 ${SWATCH_SIZE_PX}px`,
    width: SWATCH_SIZE_PX,
    height: SWATCH_SIZE_PX,
    borderRadius: theme.radius.xs,
  },
  tickLabel: {
    position: 'absolute',
    top: 0,
    height: RULER_HEIGHT_PX,
    display: 'flex',
    alignItems: 'center',
    transform: 'translateX(-50%)',
    whiteSpace: 'nowrap',
    color: theme.colors.gray[7],
    fontSize: theme.fontSizes.xs,
  },
  tickLabelRightAligned: {
    transform: 'translateX(-100%)',
  },
  gridline: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 1,
    backgroundColor: theme.colors.gray[2],
  },
  emptyLaneText: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    paddingLeft: PLOT_INSET_PX,
    color: theme.colors.gray[5],
    fontSize: theme.fontSizes.xs,
    pointerEvents: 'none',
  },
  box: {
    position: 'absolute',
    top: (ROW_HEIGHT_PX - BOX_HEIGHT_PX) / 2,
    height: BOX_HEIGHT_PX,
    minWidth: MIN_BOX_WIDTH_PX,
    boxSizing: 'border-box',
    borderRadius: theme.radius.xs,
    boxShadow: `inset 0 0 0 1px ${theme.fn.rgba(theme.black, 0.25)}`,
    cursor: 'pointer',
    '&:hover, &:focus-visible': {
      boxShadow: `inset 0 0 0 2px ${theme.colors.dark[7]}`,
      zIndex: 1,
    },
  },
  // A turn that is still running: diagonal stripes, so it reads as ongoing work rather than a result.
  boxInFlight: {
    backgroundImage: `repeating-linear-gradient(45deg, ${theme.fn.rgba(theme.white, 0.45)} 0 6px, transparent 6px 12px)`,
  },
  // A turn that never got a response: dashed, so it reads as unfinished rather than instantaneous.
  boxNoResponse: {
    border: `1px dashed ${theme.colors.dark[7]}`,
  },
  tooltipText: {
    whiteSpace: 'nowrap',
  },
}));

interface TimelineBoxProps {
  turn: TimelineTurnResponse;
  color: string;
  timeWindow: TimelineWindow;
}

function TimelineBox({turn, color, timeWindow}: TimelineBoxProps): JSX.Element {
  const {classes, cx} = useStyles();
  const {leftPercent, widthPercent} = getTurnBoxGeometry(turn, timeWindow);
  const chatName = turn.chatName ?? UNTITLED_CHAT_LABEL;

  const handleClick = (): void => {
    openExistingChat(turn.chatSessionId);
    // Semi-expanded (not expandSection's full-screen), so the foundry page stays visible.
    SidepanelActions.unexpandToSection(NavConstants.SECTIONS.AI);
    // TODO: scroll the chat to the request message (`turn.requestEventId`). Cut from the spike: it needs a
    // target event id threaded through the sidepanel state and a scroll effect in the thread that copes
    // with the thread mounting asynchronously and with streaming.
  };

  return (
    <Tooltip
      withinPortal={true}
      withArrow={true}
      openDelay={100}
      label={
        <Stack spacing={0}>
          <Text size="sm" fw={600} className={classes.tooltipText}>
            {chatName}
          </Text>
          <Text size="xs" className={classes.tooltipText}>
            {new Date(turn.startedAt).toLocaleString()}
          </Text>
          <Text size="xs" className={classes.tooltipText}>
            {formatTurnDuration(turn)}
          </Text>
        </Stack>
      }
    >
      <UnstyledButton
        aria-label={`Open chat: ${chatName}`}
        onClick={handleClick}
        className={cx(classes.box, {
          [classes.boxInFlight]: turn.state === 'in_flight',
          [classes.boxNoResponse]: turn.state === 'no_response',
        })}
        style={{
          left: `${leftPercent}%`,
          width: `${widthPercent}%`,
          backgroundColor: color,
        }}
        {...getTestProps({component: 'AgentFoundryTimeline', element: 'box', key: turn.requestEventId})}
        data-amp-safe={true}
      />
    </Tooltip>
  );
}

function TimelineRowView({
  row,
  timeWindow,
  ticks,
}: {
  row: TimelineRow;
  timeWindow: TimelineWindow;
  ticks: Array<{leftPercent: number}>;
}): JSX.Element {
  const {classes} = useStyles();
  const agentName = row.agent.name ?? UNNAMED_AGENT_LABEL;

  return (
    <div
      className={`${classes.line} ${classes.rowLine}`}
      {...getTestProps({component: 'AgentFoundryTimeline', element: 'row', key: row.agent.agentId})}
      data-amp-safe={true}
    >
      <div className={classes.gutter}>
        <span className={classes.swatch} style={{backgroundColor: row.shades[SWATCH_SHADE_INDEX]}} />
        <Stack spacing={0} miw={0}>
          <Text size="sm" fw={600} truncate={true}>
            {agentName}
          </Text>
          {row.agent.isCoordinator && (
            <Text size="xs" c="dimmed">
              Coordinator
            </Text>
          )}
        </Stack>
      </div>
      <div className={classes.lane}>
        <div className={classes.plot}>
          {ticks.map((tick) => (
            <div key={tick.leftPercent} className={classes.gridline} style={{left: `${tick.leftPercent}%`}} />
          ))}
          {row.turns.map((turn) => (
            <TimelineBox
              key={turn.requestEventId}
              turn={turn}
              timeWindow={timeWindow}
              color={row.colorByChatSessionId.get(turn.chatSessionId) ?? row.shades[0]}
            />
          ))}
        </div>
        {row.turns.length === 0 && <div className={classes.emptyLaneText}>No activity in this window</div>}
      </div>
    </div>
  );
}

function TimelineChart({
  response,
  range,
}: {
  response: AgentFoundryTimelineResponse;
  range: TimelineRange;
}): JSX.Element {
  const {classes, cx} = useStyles();
  const timeWindow = useMemo(() => getTimelineWindow(response), [response]);
  const ticks = useMemo(() => getTimelineTicks(timeWindow, range), [timeWindow, range]);
  const rows = useMemo(() => buildTimelineRows(response), [response]);

  if (rows.length === 0) {
    return <Text c="dimmed">This foundry has no agents yet, so there is no activity to show.</Text>;
  }

  return (
    <div className={classes.chart}>
      <div className={`${classes.line} ${classes.rulerLine}`}>
        <div className={classes.gutter} />
        <div className={classes.lane}>
          <div className={classes.plot}>
            {ticks.map((tick) => (
              <div
                key={tick.leftPercent}
                className={cx(classes.tickLabel, {
                  [classes.tickLabelRightAligned]: tick.leftPercent > RULER_LABEL_RIGHT_ALIGN_PERCENT,
                })}
                style={{left: `${tick.leftPercent}%`}}
              >
                {tick.label}
              </div>
            ))}
          </div>
        </div>
      </div>
      {rows.map((row, index) => (
        <TimelineRowView
          key={`${row.agent.agentId}-${index}`}
          row={row}
          timeWindow={timeWindow}
          ticks={ticks}
        />
      ))}
    </div>
  );
}

/**
 * The Timeline tab of an Agent Foundry: a calendar-style view with one lane per agent (the Coordinator
 * first, then the assigned agents), time across the x-axis, and one box per chat turn from the user's
 * request to the agent's response. A turn still running is drawn out to "now". Hovering a box shows the
 * chat's name, start and duration; clicking opens the chat in the sidepanel.
 *
 * The window is always [now - range, now], chosen from three fixed ranges and computed by the server, so
 * "now" is the right edge. It is fetched once per range selection and does not live-update.
 */
export default function AgentFoundryTimeline({agentFoundryId}: {agentFoundryId: string}): JSX.Element {
  const {classes} = useStyles();
  const [range, setRange] = useState<TimelineRange>(DEFAULT_TIMELINE_RANGE);
  const [response, setResponse] = useState<AgentFoundryTimelineResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let isCancelled = false;
    setResponse(null);
    setErrorMessage(null);
    getAgentFoundryTimeline(agentFoundryId, range)
      .then((result) => {
        if (!isCancelled) {
          setResponse(result);
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setErrorMessage('Unable to load the timeline.');
        }
      });
    return () => {
      isCancelled = true;
    };
  }, [agentFoundryId, range]);

  return (
    <div className={classes.root} {...getTestProps({component: 'AgentFoundryTimeline', element: 'root'})}>
      <Group position="apart">
        <SegmentedControl
          aria-label="Timeline range"
          role="radiogroup"
          size="xs"
          value={range}
          onChange={(value) => {
            setRange(value as TimelineRange);
          }}
          data={(Object.keys(TIMELINE_RANGES) as Array<TimelineRange>).map((value) => ({
            value,
            label: TIMELINE_RANGES[value].label,
          }))}
        />
      </Group>
      {errorMessage != null && <InlineBanner bannerType={InlineBannerType.ERROR} text={errorMessage} />}
      {errorMessage == null && response == null && <CenteredSpinner />}
      {response != null && (
        <>
          {response.truncated && (
            <InlineBanner
              bannerType={InlineBannerType.INFO}
              text={`Showing the most recent ${response.maxTurns} turns in this range.`}
            />
          )}
          <TimelineChart response={response} range={range} />
        </>
      )}
    </div>
  );
}
